import { Injectable, signal } from '@angular/core';

interface OnboardingAccessState {
  applicationStatus: string;
  onboardingStatus: string;
  creationFlow: string;
  userApplicationStatus: string;
}

const ONBOARDING_ACCESS_STATE_KEY = 'crm.onboarding-access-state';

// Map of user onboarding step names to step indices
const USER_ONBOARDING_STEP_MAP: Record<string, number> = {
  'basic details': 0,
  'basicdetails': 0,
  'authorization': 1,
  'background check': 2,
  'backgroundcheck': 2,
  'directorships': 3,
  'employment history': 4,
  'employmenthistory': 4,
  'education & qualifications': 5,
  'education&qualifications': 5,
  'education': 5,
  'educationqualifications': 5,
  'sales performance': 6,
  'salesperformance': 6,
  'financial background': 7,
  'financialbackground': 7,
  'bank & emergency': 8,
  'bank&emergency': 8,
  'bank emergency': 8,
  'bankemergency': 8,
  'health declaration': 9,
  'healthdeclaration': 9,
  'documents': 10,
  'declaration': 10,
};

@Injectable({ providedIn: 'root' })
export class CrmOnboardingAccessService {
  private state: OnboardingAccessState | null = null;
  readonly stateSignal = signal<OnboardingAccessState | null>(null);

  setState(payload: {
    applicationStatus?: string;
    onboardingStatus?: string;
    creationFlow?: string;
    userApplicationStatus?: string;
  }): void {
    const nextState: OnboardingAccessState = {
      applicationStatus: this.normalizeValue(payload.applicationStatus),
      onboardingStatus: this.normalizeValue(payload.onboardingStatus),
      creationFlow: this.normalizeValue(payload.creationFlow),
      userApplicationStatus: this.normalizeValue(payload.userApplicationStatus),
    };

    this.state = nextState;
    this.stateSignal.set(nextState);

    if (typeof window !== 'undefined') {
      sessionStorage.setItem(ONBOARDING_ACCESS_STATE_KEY, JSON.stringify(nextState));
    }
  }

  clear(): void {
    this.state = null;
    this.stateSignal.set(null);

    if (typeof window !== 'undefined') {
      sessionStorage.removeItem(ONBOARDING_ACCESS_STATE_KEY);
    }
  }

  isOperational(): boolean {
    const state = this.getState();
    return state?.applicationStatus === 'operational';
  }

  getStateSnapshot(): Readonly<OnboardingAccessState> | null {
    const state = this.getState();
    return state ? { ...state } : null;
  }

  isManualLicensedCreationFlow(): boolean {
    const state = this.getState();
    if (!state) {
      return false;
    }

    return this.toCompactValue(state.creationFlow) === 'manuallicensed';
  }

  isInviteLicensedCreationFlow(): boolean {
    const state = this.getState();
    if (!state) {
      return false;
    }

    return this.toCompactValue(state.creationFlow) === 'invitelicensed';
  }

  isInviteFreeTrialCreationFlow(): boolean {
    const state = this.getState();
    if (!state) {
      return false;
    }

    return this.toCompactValue(state.creationFlow) === 'invitefreetrial';
  }

  isAcceptAgreementStage(): boolean {
    const state = this.getState();
    if (!state) {
      return false;
    }

    const onboarding = this.toCompactValue(state.onboardingStatus);
    const creationFlow = this.toCompactValue(state.creationFlow);
    const appStatus = this.toCompactValue(state.applicationStatus);

    const isAcceptAgreement = onboarding === 'acceptagreement';
    const isLicensedCreationFlow = creationFlow === 'invitelicensed';
    const isAgreementInitiated = appStatus === 'agreementinitiated' || appStatus === 'inprogress';

    return isAcceptAgreement && isLicensedCreationFlow && isAgreementInitiated;
  }

  isUserOnboardingActive(): boolean {
    const state = this.getState();
    if (!state) {
      return false;
    }

    const status = this.toCompactValue(state.userApplicationStatus);
    return status === 'initiated' || status === 'inprogress';
  }

  isUserUnderReview(): boolean {
    const state = this.getState();
    if (!state) {
      return false;
    }

    const status = this.toCompactValue(state.userApplicationStatus);
    return status === 'onboardingreview' || status === 'finalreview';
  }

  resolveNextRoute(userOnboardingStep?: string | null): string {
    const state = this.getState();
    if (!state) {
      return '/login';
    }

    const isInviteLicensed = this.toCompactValue(state.creationFlow) === 'invitelicensed';
    const appStatus = this.toCompactValue(state.applicationStatus);
    const onboardingStatus = this.toCompactValue(state.onboardingStatus);

    if (appStatus === 'operational' || onboardingStatus === 'operational') {
      if (this.isUserOnboardingActive()) {
        const onboardingRoute = '/user-onboarding';
        // Append step parameter if provided
        if (userOnboardingStep) {
          const stepIndex = this.mapUserOnboardingStepToIndex(userOnboardingStep);
          if (stepIndex !== null) {
            return `${onboardingRoute}?step=${stepIndex}`;
          }
        }
        return onboardingRoute;
      }
      if (this.isUserUnderReview()) {
        return '/user-application-under-review';
      }
      return '/dashboard';
    }

    if (appStatus === 'inreview' && onboardingStatus === 'createhq') {
      return isInviteLicensed ? '/application-under-review' : '/manual-tenant';
    }

    if (onboardingStatus === 'createhq') {
      return '/create-hq';
    }

    if (onboardingStatus === 'formsubmission') {
      return '/manual-tenant';
    }

    if (onboardingStatus === 'acceptagreement') {
      return '/manual-tenant';
    }

    return '/manual-tenant';
  }

  /**
   * Maps user onboarding step name (e.g., "Financial Background") to step index (0-10)
   * @param stepName The name of the onboarding step
   * @returns The step index (0-10) or null if not found
   */
  private mapUserOnboardingStepToIndex(stepName: string): number | null {
    if (!stepName) {
      return null;
    }

    const normalized = this.toCompactValue(stepName);
    const stepIndex = USER_ONBOARDING_STEP_MAP[normalized];

    return typeof stepIndex === 'number' ? stepIndex : null;
  }

  private getState(): OnboardingAccessState | null {
    if (this.state) {
      return this.state;
    }

    if (typeof window === 'undefined') {
      return null;
    }

    const raw = sessionStorage.getItem(ONBOARDING_ACCESS_STATE_KEY);
    if (!raw) {
      return null;
    }

    try {
      this.state = JSON.parse(raw) as OnboardingAccessState;
      this.stateSignal.set(this.state);
      return this.state;
    } catch {
      sessionStorage.removeItem(ONBOARDING_ACCESS_STATE_KEY);
      return null;
    }
  }

  private normalizeValue(value: unknown): string {
    if (typeof value !== 'string') {
      return '';
    }

    return value.trim().toLowerCase();
  }

  private toCompactValue(value: string): string {
    return String(value || '')
      .trim()
      .toLowerCase()
      .replace(/\s+/g, '');
  }
}